import {
  PUZZLE_BOARD_COLUMNS,
  PUZZLE_BOARD_HEIGHT,
  PUZZLE_BOARD_ROWS,
  PUZZLE_BOARD_WIDTH,
  PUZZLE_BUBBLE_RADIUS,
  PUZZLE_DANGER_LINE_Y,
  PUZZLE_HEX_ROW_HEIGHT,
  PUZZLE_ROUND_TICKS,
  PUZZLE_SHOOTER_X,
  PUZZLE_SHOOTER_Y,
  PUZZLE_TICK_RATE,
  type PuzzleBubbleKind,
  type PuzzleEvent,
  type PuzzleMatchState
} from "../simulation";

export type PuzzleTeamView = "blue" | "orange";

export type PuzzleBubbleKindView = PuzzleBubbleKind;

export interface PuzzlePlayerView {
  id: string;
  name: string;
  team: PuzzleTeamView;
  kind: "human" | "bot";
  connected: boolean;
}

export interface PuzzleCellView {
  row: number;
  col: number;
  color: string;
}

export interface PuzzleShotView {
  x: number;
  y: number;
  color: string;
  kind: PuzzleBubbleKindView;
}

export interface PuzzleHeldBubbleView {
  color: string;
  kind: PuzzleBubbleKindView;
}

export interface PuzzleBoardView {
  playerId: string;
  name: string;
  team: PuzzleTeamView;
  accent: PuzzleTeamView;
  kind: "human" | "bot";
  connected: boolean;
  cells: PuzzleCellView[];
  activeShot: PuzzleShotView | null;
  aim: number;
  currentColor: string;
  nextColor: string;
  currentKind: PuzzleBubbleKindView;
  nextKind: PuzzleBubbleKindView;
  heldBubble: PuzzleHeldBubbleView | null;
  holdLocked: boolean;
  combo: number;
  bestCombo: number;
  dangerLineY: number;
  pendingPressureRows: number;
  pendingAttackBubbles: number;
  status: "active" | "lost" | "cleared";
  score: number;
  shotsResolved: number;
}

export interface PuzzleViewModel {
  id: string;
  mode: "1v1" | "2v2";
  phase: string;
  tick: number;
  round: number;
  roundTimeSeconds: number;
  overtime: boolean;
  overtimeTicks: number;
  roundWins: Record<PuzzleTeamView, number>;
  scores: Record<PuzzleTeamView, number>;
  roundWinner: PuzzleTeamView | null;
  matchWinner: PuzzleTeamView | null;
  players: PuzzlePlayerView[];
  boards: PuzzleBoardView[];
  primaryPlayerId: string;
  logicalBoard: {
    width: number;
    height: number;
    columns: number;
    rows: number;
    bubbleRadius: number;
    hexRowHeight: number;
    shooterX: number;
    shooterY: number;
    dangerLineY: number;
  };
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord {
  return value !== null && typeof value === "object" ? value as UnknownRecord : {};
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringValue(value: unknown, fallback: string): string {
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

function numberValue(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function normalizeTeam(value: unknown, index = 0): PuzzleTeamView {
  const team = typeof value === "string" ? value.toLowerCase() : value;
  if (team === "orange" || team === "coral" || team === "b" || team === 1) return "orange";
  if (team === "blue" || team === "cyan" || team === "a" || team === 0) return "blue";
  return index % 2 === 0 ? "blue" : "orange";
}

function normalizeKind(value: unknown): "human" | "bot" {
  const kind = typeof value === "string" ? value.toLowerCase() : "human";
  return kind === "bot" || kind === "ai" ? "bot" : "human";
}

function normalizeStatus(value: unknown): "active" | "lost" | "cleared" {
  return value === "lost" || value === "cleared" ? value : "active";
}

export function normalizePuzzleBubbleKind(value: unknown): PuzzleBubbleKindView {
  const kind = typeof value === "string" ? value.toLowerCase().replaceAll("-", "_") : "normal";
  if (kind === "color_burst" || kind === "color" || kind === "rainbow" || kind === "star") {
    return "color_burst";
  }
  if (kind === "row_burst" || kind === "row" || kind === "horizontal") return "row_burst";
  if (kind === "column_burst" || kind === "column" || kind === "vertical") return "column_burst";
  return "normal";
}

export function puzzleBubbleKindLabel(kind: PuzzleBubbleKindView): string {
  if (kind === "color_burst") return "COLOR";
  if (kind === "row_burst") return "ROW";
  if (kind === "column_burst") return "COLUMN";
  return "NORMAL";
}

function teamNumber(source: unknown, team: PuzzleTeamView): number {
  const values = record(source);
  const candidates = team === "blue"
    ? [values.blue, values.cyan, values.A, values.a]
    : [values.orange, values.coral, values.B, values.b];
  return candidates.reduce<number>((result, candidate) =>
    result !== 0 ? result : numberValue(candidate, 0), 0);
}

function normalizeCells(source: unknown): PuzzleCellView[] {
  const rows = array(source);
  if (rows.some(Array.isArray)) {
    const result: PuzzleCellView[] = [];
    rows.forEach((rowValue, row) => {
      array(rowValue).forEach((cellValue, col) => {
        if (typeof cellValue === "string") {
          result.push({ row, col, color: cellValue });
          return;
        }
        const cell = record(cellValue);
        const color = stringValue(cell.color, "");
        if (color) result.push({ row, col, color });
      });
    });
    return result;
  }
  return rows.flatMap((value, index) => {
    const cell = record(value);
    const color = stringValue(cell.color, "");
    if (!color) return [];
    return [{
      row: numberValue(cell.row, Math.floor(index / PUZZLE_BOARD_COLUMNS)),
      col: numberValue(cell.col ?? cell.column, index % PUZZLE_BOARD_COLUMNS),
      color
    }];
  });
}

function normalizeAccent(sideByTeam: UnknownRecord, team: PuzzleTeamView): PuzzleTeamView {
  const side = sideByTeam[team];
  if (side === "right" || side === "orange" || side === "coral") return "orange";
  if (side === "left" || side === "blue" || side === "cyan") return "blue";
  return team;
}

function inferPrimaryPlayer(players: PuzzlePlayerView[], preferredPlayerId?: string): string {
  if (preferredPlayerId && players.some((player) => player.id === preferredPlayerId)) return preferredPlayerId;
  return players.find((player) => player.kind === "human")?.id ?? players[0]?.id ?? "p1";
}

/**
 * The only authoritative-state to render-model mapping. Tolerant reads keep
 * delayed/admin snapshots usable across additive protocol changes while all
 * gameplay decisions remain in the simulation.
 */
export function toPuzzleViewModel(
  state: PuzzleMatchState,
  preferredPlayerId?: string
): PuzzleViewModel {
  const raw = record(state);
  const rawPlayers = array(raw.players);
  const players = rawPlayers.map((value, index) => {
    const player = record(value);
    return {
      id: stringValue(player.id, `p${index + 1}`),
      name: stringValue(player.name, `P${index + 1}`),
      team: normalizeTeam(player.team, index),
      kind: normalizeKind(player.kind),
      connected: booleanValue(player.connected, true)
    } satisfies PuzzlePlayerView;
  });
  const primaryPlayerId = inferPrimaryPlayer(players, preferredPlayerId);
  const rawBoards = record(raw.boards);
  const sideByTeam = record(raw.sideByTeam);
  const boardIds = players.length > 0 ? players.map((player) => player.id) : Object.keys(rawBoards);
  const boards = boardIds.map((playerId, index) => {
    const player = players.find((candidate) => candidate.id === playerId) ?? {
      id: playerId,
      name: `P${index + 1}`,
      team: normalizeTeam(undefined, index),
      kind: "bot" as const,
      connected: true
    };
    const board = record(rawBoards[playerId]);
    const shot = record(board.activeShot);
    const shotColor = stringValue(shot.color, "");
    const currentKind = normalizePuzzleBubbleKind(board.currentKind ?? board.currentBubbleKind);
    const nextKind = normalizePuzzleBubbleKind(board.nextKind ?? board.nextBubbleKind);
    const heldBubble = record(board.heldBubble);
    const heldColor = stringValue(heldBubble.color, "");
    return {
      playerId,
      name: player.name,
      team: board.team === undefined ? player.team : normalizeTeam(board.team, index),
      accent: normalizeAccent(sideByTeam, player.team),
      kind: player.kind,
      connected: player.connected,
      cells: normalizeCells(board.cells),
      activeShot: shotColor ? {
        x: numberValue(shot.x, PUZZLE_SHOOTER_X),
        y: numberValue(shot.y, PUZZLE_SHOOTER_Y),
        color: shotColor,
        kind: normalizePuzzleBubbleKind(shot.kind ?? shot.bubbleKind ?? currentKind)
      } : null,
      aim: numberValue(board.aim, 0),
      currentColor: stringValue(board.currentColor, "cyan"),
      nextColor: stringValue(board.nextColor, "coral"),
      currentKind,
      nextKind,
      heldBubble: heldColor ? {
        color: heldColor,
        kind: normalizePuzzleBubbleKind(heldBubble.kind ?? heldBubble.bubbleKind)
      } : null,
      holdLocked: booleanValue(board.holdLocked, false),
      combo: Math.max(0, numberValue(board.combo, 0)),
      bestCombo: Math.max(0, numberValue(board.bestCombo ?? board.maxCombo, 0)),
      dangerLineY: numberValue(board.dangerLineY, PUZZLE_DANGER_LINE_Y),
      pendingPressureRows: Math.max(0, numberValue(board.pendingPressureRows, 0)),
      pendingAttackBubbles: Math.max(0, numberValue(
        board.pendingAttackBubbles ?? board.pendingPressureBubbles ?? board.pendingBubbles,
        0
      )),
      status: normalizeStatus(board.status),
      score: Math.max(0, numberValue(board.score, 0)),
      shotsResolved: Math.max(0, numberValue(board.shotsResolved, 0))
    } satisfies PuzzleBoardView;
  });
  const mode = stringValue(raw.mode, players.length > 2 ? "team" : "duel").toLowerCase();
  const phase = stringValue(raw.phase, "countdown");
  const overtime = booleanValue(raw.overtime, false);
  const rawRoundTicks = numberValue(raw.roundTicksRemaining, PUZZLE_ROUND_TICKS);

  return {
    id: stringValue(raw.id, "foam-pop"),
    mode: mode === "team" || mode === "2v2" || mode.includes("2") ? "2v2" : "1v1",
    phase,
    tick: numberValue(raw.tick, 0),
    round: Math.max(1, numberValue(raw.round, 1)),
    roundTimeSeconds: Math.max(0, rawRoundTicks / PUZZLE_TICK_RATE),
    overtime,
    overtimeTicks: Math.max(0, numberValue(raw.overtimeTicks, 0)),
    roundWins: {
      blue: teamNumber(raw.roundWins, "blue"),
      orange: teamNumber(raw.roundWins, "orange")
    },
    scores: {
      blue: teamNumber(raw.scores, "blue"),
      orange: teamNumber(raw.scores, "orange")
    },
    roundWinner: raw.roundWinner === null || raw.roundWinner === undefined
      ? null
      : normalizeTeam(raw.roundWinner),
    matchWinner: raw.matchWinner === null || raw.matchWinner === undefined
      ? null
      : normalizeTeam(raw.matchWinner),
    players,
    boards,
    primaryPlayerId,
    logicalBoard: {
      width: PUZZLE_BOARD_WIDTH,
      height: PUZZLE_BOARD_HEIGHT,
      columns: PUZZLE_BOARD_COLUMNS,
      rows: PUZZLE_BOARD_ROWS,
      bubbleRadius: PUZZLE_BUBBLE_RADIUS,
      hexRowHeight: PUZZLE_HEX_ROW_HEIGHT,
      shooterX: PUZZLE_SHOOTER_X,
      shooterY: PUZZLE_SHOOTER_Y,
      dangerLineY: PUZZLE_DANGER_LINE_Y
    }
  };
}

const COLOR_STYLES: Readonly<Record<string, { fill: number; dark: number; light: number; css: string; shape: string }>> = {
  cyan: { fill: 0x2fdddc, dark: 0x0a6672, light: 0xc5ffff, css: "#2fdddc", shape: "triangle" },
  coral: { fill: 0xff6f70, dark: 0x86344f, light: 0xffd4c9, css: "#ff6f70", shape: "diamond" },
  lime: { fill: 0xb7ef64, dark: 0x4b7228, light: 0xf1ffc8, css: "#b7ef64", shape: "ring" },
  violet: { fill: 0xa987ff, dark: 0x4e3a91, light: 0xeadfff, css: "#a987ff", shape: "hex" },
  gold: { fill: 0xffcb54, dark: 0x8a5c19, light: 0xfff1b9, css: "#ffcb54", shape: "cross" },
  blue: { fill: 0x55a7ff, dark: 0x23568f, light: 0xd6ebff, css: "#55a7ff", shape: "triangle" },
  orange: { fill: 0xff8a55, dark: 0x8d4529, light: 0xffddc8, css: "#ff8a55", shape: "diamond" },
  pink: { fill: 0xff76bc, dark: 0x913f70, light: 0xffd7ec, css: "#ff76bc", shape: "ring" },
  green: { fill: 0x62e195, dark: 0x24734c, light: 0xd5ffe5, css: "#62e195", shape: "hex" },
  purple: { fill: 0xa987ff, dark: 0x4e3a91, light: 0xeadfff, css: "#a987ff", shape: "cross" }
};

const FALLBACK_COLOR_STYLES = [
  COLOR_STYLES.cyan,
  COLOR_STYLES.coral,
  COLOR_STYLES.lime,
  COLOR_STYLES.violet,
  COLOR_STYLES.gold
] as const;

export function puzzleColorStyle(color: string): NonNullable<(typeof COLOR_STYLES)[string]> {
  const normalized = color.toLowerCase();
  const known = COLOR_STYLES[normalized];
  if (known) return known;
  let hash = 0;
  for (const character of normalized) hash = (hash * 31 + character.charCodeAt(0)) | 0;
  return FALLBACK_COLOR_STYLES[Math.abs(hash) % FALLBACK_COLOR_STYLES.length] ?? COLOR_STYLES.cyan!;
}

export interface PuzzleEventSummary {
  label: string;
  tone: "blue" | "orange" | "reward" | "danger" | "neutral";
  playerId?: string;
}

export function summarizePuzzleEvent(event: PuzzleEvent): PuzzleEventSummary | null {
  const raw = record(event);
  const type = stringValue(raw.type, "").toLowerCase();
  const playerId = stringValue(
    type === "pressure_queued"
      ? raw.fromPlayerId ?? raw.playerId ?? raw.boardId
      : raw.playerId ?? raw.boardId ?? raw.targetPlayerId,
    ""
  );
  const team = normalizeTeam(raw.team ?? raw.winner, 0);
  let summary: Omit<PuzzleEventSummary, "playerId"> | null = null;
  if (type === "match_ended") summary = { label: `${team.toUpperCase()} WINS`, tone: team };
  else if (type === "round_ended") summary = { label: `${team.toUpperCase()} ROUND`, tone: team };
  else if (type === "dropped") summary = { label: "CLUSTER DROP", tone: "reward" };
  else if (type === "matched") summary = { label: "FOAM POP", tone: "reward" };
  else if (type === "bubble_held") summary = { label: "ORB HELD", tone: "neutral" };
  else if (type === "special_triggered") {
    const kind = normalizePuzzleBubbleKind(raw.kind ?? raw.bubbleKind);
    summary = { label: `${puzzleBubbleKindLabel(kind)} BURST`, tone: "reward" };
  }
  else if (type === "combo_changed" || type === "combo_updated") {
    const combo = Math.max(0, numberValue(raw.combo, 0));
    summary = { label: combo > 0 ? `COMBO ×${combo}` : "COMBO READY", tone: "reward" };
  }
  else if (type === "combo_broken") {
    const previousCombo = Math.max(0, numberValue(raw.previousCombo, 0));
    summary = {
      label: previousCombo > 0 ? `COMBO ×${previousCombo} LOST` : "COMBO LOST",
      tone: "danger"
    };
  }
  else if (type === "board_cleared") summary = { label: "BOARD CLEAR · KEEP GOING", tone: "reward" };
  else if (type === "pressure_queued") {
    const rows = Math.max(0, numberValue(raw.rows, 0));
    const bubbles = Math.max(0, numberValue(raw.bubbles, 0));
    const load = [rows > 0 ? `+${rows}R` : "", bubbles > 0 ? `+${bubbles}B` : ""]
      .filter(Boolean)
      .join(" ");
    summary = { label: `ATTACK SENT${load ? ` · ${load}` : ""}`, tone: "reward" };
  }
  else if (type === "pressure_applied") summary = { label: "PRESSURE IN", tone: "danger" };
  else if (type === "board_lost") summary = { label: "LINE BREACH", tone: "danger" };
  if (!summary) return null;
  return playerId ? { ...summary, playerId } : summary;
}
